import { PropType, reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { uniqBy } from 'lodash-es';
import ClientProvisionDocumentApiClient from '@/api/ClientProvisionDocumentApiClient';
import ClientContractApiClient from '@/api/ClientContractApiClient';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { useModal, useNotif, useValidation } from '@orion.ui/orion';

type Props = SetupProps<typeof ContractAsideSignatureClientSetupService.props>

type EntityOption = {
	delegateName: string,
	id: number,
	name: string,
}

type FilteredDocument = {
	id: number;
	clientCompanyId: number;
	reference?: string | undefined;
	clientCompanyName?: string | undefined;
	delegateName?: string | undefined;
}

export default class ContractAsideSignatureClientSetupService extends BaseSetupService<Props> {
	static readonly props = {
		documentIds: {
			type: Array as PropType<number[]>,
			default: () => [],
		},
		documentType: {
			type: Number,
			default: DOCUMENT_TYPE.None,
		},
	};

	private readonly _aside?: OrionAside;

	private readonly state = reactive({
		documents: [] as IClientProvisionDocumentForConfirmAsideModel[] | IClientContractForConfirmAsideModel[],
		termsAcceptation: false,
		selectedEntities: [] as EntityOption[],
		search: '',
	});

	get termsAcceptation () { return this.state.termsAcceptation;}
	set termsAcceptation (val) { this.state.termsAcceptation = val;}
	get documents () { return this.state.documents;}
	get search () { return this.state.search;}
	set search (val) { this.state.search = val;}
	get selectedEntities () { return this.state.selectedEntities;}
	set selectedEntities (val) { this.state.selectedEntities = val;}

	get entitiesOptions () {
		return uniqBy(this.state.documents.map((x) => {
			return {
				delegateName: x.delegateName,
				id: x.clientCompanyId,
				name: x.clientCompanyName,
			} as EntityOption;
		}), x => x.id);
	}

	get entities () {
		return this.state.selectedEntities ? this.state.selectedEntities.map(x => ({
			...x,
			documents: this.entityDocuments(x),
		})) : [];
	}

	get filteredDocuments () {
		return this.entities.map(x => x.documents).reduce((prev, current) => [...prev, ...current]);
	}

	get apiSignature () {
		switch (this.props.documentType) {
		case DOCUMENT_TYPE.ClientContract:
			return ClientContractApiClient;
		case DOCUMENT_TYPE.ClientProvisionDocument:
			return ClientProvisionDocumentApiClient;
		default:
			return null;
		}
	}

	get title () {
		switch (this.props.documentType) {
		case DOCUMENT_TYPE.ClientContract:
			return `Signature de contrats de mise à disposition`;
		case DOCUMENT_TYPE.ClientProvisionDocument:
			return `Signature de documents mis à disposition`;
		default:
			return `Signature de documents`;
		}
	}

	get documentName () {
		switch (this.props.documentType) {
		case DOCUMENT_TYPE.ClientContract:
			return `contrat`;
		case DOCUMENT_TYPE.ClientProvisionDocument:
			return `document`;
		default:
			return `document`;
		}
	}

	validateField = () => this.termsAcceptation === true;

	form = reactive({ terms: this.termsAcceptation });

	validator = useValidation(this.form, { terms: this.validateField });

	constructor (props: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;
	}


	protected async onBeforeMountAsync () {
		await this.getDocumentsAsync();
		this.state.selectedEntities = this.entitiesOptions;
		setTimeout(this.checkDelegates.bind(this), 300);
	}

	async getDocumentsAsync () {
		this._aside?._loader()?.show(`Chargements des documents...`);
		if (!this.apiSignature) {
			this._aside?._loader()?.hide();
			return;
		}

		const { data } = await this.apiSignature.listForConfirm(this.props.documentIds);
		this._aside?._loader()?.hide();
		this.state.documents = data;

		if (!this.documents || !this.documents.length) {
			useNotif.success('Aucun document à signer');
			this._aside?.close();
		}
	}

	entityDocuments (entity: EntityOption) {
		let filteredDocuments = this.documents.filter(x => x.clientCompanyId === entity.id);

		if (this.search && this.search.trim().length) {
			filteredDocuments = filteredDocuments.filter(x => x.reference?.includes(this.search));
		}

		return filteredDocuments;
	}

	sign () {
		if (!this.state.termsAcceptation) {
			this.validator.showValidationState();
			return;
		}

		this._aside?._loader()?.show('Vos documents sont en cours de préparation pour la signature...');
		this._aside?.trigger('sign', this.filteredDocuments.map(x => ({
			id: x.id,
			status: true,
		})));
	}

	openDoc (document: FilteredDocument) {
		switch (this.props.documentType) {
		case DOCUMENT_TYPE.ClientContract:
		{
			const routeData = this.router.resolve({
				name: 'ClientContractDetails',
				params: { id: document.id },
			});
			window.open(routeData.href, '_blank');
			break;
		}
		case DOCUMENT_TYPE.ClientProvisionDocument:
			this.apiSignature?.view(`?ids=${document.id}`);
			break;
		default:
			break;
		}

	}

	checkDelegates () {
		const entitiesWithoutDelegates = this.entities?.filter(x => x.delegateName.trim().length === 0);

		if (entitiesWithoutDelegates.length) {
			this._aside?._loader()?.show(`Il manque ${'représentant légal'.pluralize(entitiesWithoutDelegates.length)}`);

			let message = entitiesWithoutDelegates.length > 1
				? `<p>Les entités <b>${entitiesWithoutDelegates.map(x => x.name).join(', ')}</b> n'ont pas de représentant légal.</p>`
				: `<p>L'entité <b>${entitiesWithoutDelegates[0].name}</b> n'a pas de représentant légal.</p>`;

			message += `<p>Toutes les entités doivent avoir un représentant légal pour pouvoir signer des documents.</p>`;

			const actions = [
				{
					outline: true,
					color: 'danger' as Orion.Color,
					label: `Annuler`,
					callback: (Modal: OrionModal) => {
						Modal.close();
						this._aside?.close();
					},
				},
			];

			if (this.self.can('CompanySettingUpdate')) {
				actions.unshift({
					outline: false,
					color: 'info',
					label: `Définir les représentants légaux`,
					callback: (Modal) => {
						Modal.close();
						this._aside?.close();
						this.router.push({
							name: 'Settings',
							query: { tab: 'delegates' },
						});
					},
				});
			}

			useModal({
				size: 'sm',
				title: `Attention`,
				message,
				hideClose: true,
				hideOnOverlayClick: false,
				actions,
			});
		}
	}
}
